import React, { useState, useEffect } from 'react';
import { Card, Image, Spin, Typography, Space, Tooltip, Button } from 'antd';
import { HeartOutlined, StarOutlined, EyeOutlined, FireOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

interface FeaturedProduct {
  id: number;
  title: string;
  images: string[];
  thumbnail_url?: string;
  like_count: number;
  favorite_count: number;
  view_count: number;
  is_featured: boolean;
  category: string;
}

const FeaturedCarousel: React.FC = () => {
  const [products, setProducts] = useState<FeaturedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await fetch('/api/printed_products/featured?limit=15');
      if (!response.ok) {
        throw new Error('获取推荐产品失败');
      }
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('获取推荐产品失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (product: FeaturedProduct) => {
    navigate(`/products/${product.id}`);
  };

  const scrollLeft = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const scrollRight = () => {
    setCurrentIndex((prev) => Math.min(products.length - 1, prev + 1));
  };

  // 获取分类名称
  const getCategoryName = (category: string) => {
    const categories: { [key: string]: string } = {
      'decorative': '装饰品',
      'functional': '功能件',
      'toy': '玩具',
      'art': '艺术品',
      'tool': '工具',
      'other': '其他',
    };
    return categories[category] || category;
  };

  // 获取分类颜色
  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'decorative': '#d32f2f',
      'functional': '#1890ff',
      'toy': '#ff9800',
      'art': '#9c27b0',
      'tool': '#4caf50',
      'other': '#795548',
    };
    return colors[category] || '#666';
  };

  if (loading) {
    return (
      <div style={{
        width: '100%',
        padding: '20px 0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
        background: 'linear-gradient(135deg, #fff5f5 0%, #ffeef2 100%)',
        borderRadius: '12px',
        marginBottom: '24px'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <div style={{
      width: '100%',
      padding: '16px 0',
      background: 'linear-gradient(135deg, #fff5f5 0%, #ffeef2 100%)',
      borderRadius: '12px',
      marginBottom: '24px',
      position: 'relative'
    }}>
      {/* 标题区域 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        padding: '0 20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FireOutlined style={{ color: '#d32f2f', fontSize: '20px' }} />
          <Title level={4} style={{ margin: 0, color: '#333', fontWeight: 'bold' }}>
            热门推荐
          </Title>
        </div>
        <Text type="secondary" style={{ fontSize: '14px' }}>
          {products.length} 个精选作品
        </Text>
      </div>

      {/* 滚动容器 */}
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
      }}>
        {/* 左箭头按钮（桌面端显示） */}
        <Button
          type="text"
          icon={<LeftOutlined />}
          onClick={scrollLeft}
          disabled={currentIndex === 0}
          style={{
            display: currentIndex > 0 ? 'flex' : 'none',
            zIndex: 2,
            width: '36px',
            height: '48px',
            borderRadius: '8px 0 0 8px',
            background: 'rgba(0, 0, 0, 0.1)',
            minWidth: '36px',
          }}
        />

        {/* 产品卡片列表 */}
        <div style={{
          display: 'flex',
          gap: '16px',
          overflowX: 'auto',
          padding: '0 20px',
          scrollBehavior: 'smooth',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          width: '100%',
          alignItems: 'stretch'
        }}>
          {products.map((product, index) => (
            <Card
              key={product.id}
              hoverable
              onClick={() => handleCardClick(product)}
              style={{
                minWidth: '160px',
                maxWidth: '200px',
                flex: '0 0 auto',
                borderRadius: '10px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                cursor: 'pointer',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
              }}
              bodyStyle={{ padding: '8px' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
              }}
            >
              {/* 图片区域 */}
              <div style={{
                position: 'relative',
                width: '100%',
                paddingTop: '100%',
                overflow: 'hidden',
                borderRadius: '8px',
                marginBottom: '8px',
                background: '#f5f5f5'
              }}>
                {(product.images && product.images.length > 0) || product.thumbnail_url ? (
                  <Image
                    alt={product.title}
                    src={product.thumbnail_url || product.images?.[0]}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    preview={false}
                  />
                ) : (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: getCategoryColor(product.category),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FireOutlined style={{ fontSize: '32px', color: 'rgba(255, 255, 255, 0.5)' }} />
                  </div>
                )}

                {/* 精选标签 */}
                {product.is_featured && (
                  <div style={{
                    position: 'absolute',
                    top: '6px',
                    left: '6px',
                    background: 'linear-gradient(135deg, #ffd700, #ffeb3b)',
                    color: '#fff',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: '0 2px 4px rgba(255, 215, 0, 0.3)'
                  }}>
                    <StarOutlined style={{ fontSize: '10px' }} />
                    精选
                  </div>
                )}

                {/* 分类标签 */}
                <div style={{
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  background: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '10px'
                }}>
                  {getCategoryName(product.category)}
                </div>
              </div>

              {/* 标题 */}
              <Tooltip title={product.title}>
                <Title level={5} style={{
                  margin: '0 0 8px 0',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '1.3',
                  minHeight: '18px'
                }}>
                  {product.title}
                </Title>
              </Tooltip>

              {/* 统计数据 */}
              <Space size={8} style={{ fontSize: '12px', color: '#666', flexWrap: 'wrap' }}>
                <Tooltip title="点赞数">
                  <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <HeartOutlined style={{ color: '#ff6b6b', fontSize: '11px' }} />
                    {product.like_count}
                  </span>
                </Tooltip>
                <Tooltip title="观看数">
                  <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <EyeOutlined style={{ color: '#1890ff', fontSize: '11px' }} />
                    {product.view_count}
                  </span>
                </Tooltip>
              </Space>
            </Card>
          ))}
        </div>

        {/* 右箭头按钮（桌面端显示） */}
        <Button
          type="text"
          icon={<RightOutlined />}
          onClick={scrollRight}
          disabled={currentIndex >= products.length - 1}
          style={{
            display: currentIndex < products.length - 1 ? 'flex' : 'none',
            zIndex: 2,
            width: '36px',
            height: '48px',
            borderRadius: '0 8px 8px 0',
            background: 'rgba(0, 0, 0, 0.1)',
            minWidth: '36px',
          }}
        />
      </div>

      {/* 添加滚动条样式 */}
      <style>{`
        /* 隐藏滚动条 */
        div[style*="overflowX"]::-webkit-scrollbar {
          display: none;
        }
        div[style*="overflowX"]::-moz-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default FeaturedCarousel;
